import { orgApi } from "@/utils/Api.util";
import { PaginationState } from "@/models/PaginationState.model";
import { FilterDef } from "./filters/FilterDef";
import { OrderParameter } from "@/models/OrderParameter.model";
import { _ActionsTree, _GettersTree, _StoreWithGetters, _StoreWithState, DefineStoreOptions, PiniaCustomProperties } from "pinia";
import { ApiErrorMessageBag } from "./errors/ApiErrorMessageBag.ts";
import { ref, UnwrapRef } from "vue";
import { Comparable } from "@/interfaces/Comparable.interface.ts";


export interface AbstractStoreState<M, F extends FilterDef> {

	page: PaginationState | null,
	order: OrderParameter,
	items: M[],
	errors: ApiErrorMessageBag | null,

	loading: boolean,
	loadingMore?: boolean,
	loadedOnce: boolean,

	_loadOncePromise: Promise<void> | null,
	_loadMorePromise?: Promise<void> | null,
	_onStoreChangeTimeout?: ReturnType<typeof setTimeout> | null,

	_defaultFilter: F | null,
	_initialFilter: F | null,
	_filter: F | null,
	_loadRequestId: number,
}

export interface Model extends Comparable {
	id?: string;
	hydrateWithApiData(data: any): void;
	asApiDataObject(): any;
	clone(): Model;
}

export const Method = {
	INDEX: 		'index',
	VIEW: 		'view',
	CREATE:		'create',
	LINK:		'link',
	UNLINK:		'unlink',
	UPDATE:		'update',
	DELETE:		'delete',
	RESTORE:	'restore',
	DUPLICATE:	'duplicate',
}

export interface AbstractBaseStoreActionsInterface<M extends Model, F> extends _ActionsTree {
	loadOnce(): Promise<void>;
	load(awaitNewDataBeforeResettingList?: boolean): Promise<void>;
	loadMore(): Promise<void>;
	loadFirstPage(): Promise<void>;
	goToPage(page: PaginationState): Promise<void>;
	delete(model: M): Promise<void>;
	refresh(model: M): Promise<void>;
	loadById(id: string): Promise<M>;

	getCurrentFilter(): F;
	getItems(asStoreObjectReference?: boolean, orderParameter?: OrderParameter | null): UnwrapRef<M[]>;
	find(id: string): M | null;
	findAll(ids: string[]): M[];
	updateStoreItem(model: M): M;
	updateOrAddStoreItem(model: M): M;
	addStoreItem(model: M): M;
	removeStoreItem(model: M): void;
	clearErrors(): void;

	applyFilter(filter: F): Promise<F>;
	resetFilter(): Promise<F>;

	applyOrder(order: OrderParameter): void;
	shouldSortLocally(): boolean;
	sortLocally(): void;

	superResetFilter(): Promise<F>;
	superApplyFilter(filter: F): Promise<F>;
	superSave(model: M): Promise<void>;

	onAddStoreItem(model: M): void;
	onRemoveStoreItem(model: M): void;
	onUpdateStoreItem(model: M): void;
	onStoreChanged(): void;
}

/**
 * Interface that overrides the default store options to be able to define the type of the state (including the genric type T).
 */
type AbstractBaseStoreOptionsInterface<
	M extends Model,
	S extends AbstractStoreState<M, F>,
	F extends FilterDef
> = Omit<DefineStoreOptions<string, AbstractStoreState<M, F>, _GettersTree<S>, AbstractBaseStoreActionsInterface<M, F>>, 'id'> & {
		// the 'any' in the action tree is not correct and should instead be some kind of ActionTree type that describes
		// the content of the action property, but I haven't managed to get that working in the generic typings yet.

		// The Omit<{},'id'> is to tell typescript that the id property is not required in this particular store.

		state: <S>(baseState?: any) => S;

		// Re-define actions as we are sure it exists.
		// (this is to replace the actions?: ... in DefineStoreOptions in pinia.d.ts)
		actions: AbstractBaseStoreActionsInterface<M, F> &
			ThisType<
				AbstractBaseStoreActionsInterface<M, F> &
				UnwrapRef<S> &
				_StoreWithState<'id', S, _GettersTree<S>, AbstractBaseStoreActionsInterface<M, F>> &
				_StoreWithGetters<_GettersTree<S>> &
				PiniaCustomProperties
			>;
}

/**
 *
 */
export function AbstractBaseStore<
	M extends Model,
	S extends AbstractStoreState<M, F>,
	F extends FilterDef
>(): AbstractBaseStoreOptionsInterface<M, S, F> {
	return {

		/**
		 * Build the initial Pinia state for a concrete store by combining the base store defaults
		 * with any custom state provided by the store implementation.
		 *
		 * `baseState` may contain normal state properties as well as a few special configuration
		 * inputs that are interpreted before the final state object is returned:
		 * - `defaultFilter`: cloned and stored as `_defaultFilter`; if omitted, an empty `FilterDef`
		 *   instance is created.
		 * - `initialFilter`: cloned and used as the initial active filter; if omitted, a clone of the
		 *   resolved `defaultFilter` is used instead.
		 * - `order`: used as the initial ordering; if omitted, the store defaults to
		 *   `new OrderParameter('id')`.
		 *
		 * The returned object always starts with the base pagination / loading / collection state,
		 * then assigns the resolved `order`, and stores the resolved filter values internally as:
		 * - `_defaultFilter`: the baseline filter restored by `resetFilter()`.
		 * - `_initialFilter`: the startup filter restored by `applyInitialFilter()`.
		 * - `_filter`: the currently active filter sent with index requests, initialised from the same
		 *   resolved filter as `_initialFilter`.
		 *
		 * Note that filters are cloned so the store keeps its own copies, while `order` is reused as
		 * provided. Also note that `baseState` is mutated: `defaultFilter` and `initialFilter` are
		 * deleted before the final `...baseState` spread, so those temporary inputs do not become
		 * public store state properties. `order` is not deleted, so it remains part of the returned
		 * public state. All remaining properties in `baseState` are spread last and
		 * may override the base defaults when needed.
		 *
		 * @param baseState Custom initial state plus optional `defaultFilter`, `initialFilter`, and
		 * `order` configuration values.
		 * @returns The fully constructed initial store state.
		 */
		state: <S>(baseState: any = {}): S => {

			const defaultFilter = baseState.defaultFilter ? baseState.defaultFilter.clone() : new FilterDef([]);
			const filter = baseState.initialFilter ? baseState.initialFilter.clone() : defaultFilter.clone();
			const order = baseState.order ? baseState.order : new OrderParameter('id');

			delete baseState.defaultFilter;
			delete baseState.initialFilter;

			return {
				page: 			null,
				items: 			[],
				order: 			order,
				errors: 	null,

				loading:		false,
				loadedOnce:		false,
				_loadOncePromise: null,
				_loadMorePromise: null,

				_defaultFilter:	defaultFilter,
				_initialFilter:	filter,
				_filter: 		filter,
				_loadRequestId: 0,

				_onStoreChangeTimeout: null,

				...baseState
			} as unknown as S;

		},

		getters: {

			/**
			 * Get the API path for the store.
			 *
			 * This getter must be implemented in the actual store and must return the base path of the
			 * entity that needs to be loaded.
			 *
			 * Alternatively, apiUrl() can be implemented to return specific URLs for each method.
			 *
			 * @param state
			 */
			apiPath(state: S): string {
				throw new Error('apiPath not implemented');
			},

			apiClient(state: S): any {
				// Return the api client that should be used to make requests.
				// By default, this is the orgApi client.
				return orgApi;
			},

			apiEntityType(state: S): string {
				// Probably similar to the apiPath but without dashes and no plural ending.
				// But feel free to override this in the store. :)
				// Example: evaluation-levels => evaluationlevel

				// @ts-ignore Typescript doesn't know that apiPath is implemented in the actual store.
				return this.apiPath.split('/').pop().replace(/-/g, '').replace(/s$/, '');
			},

			/**
			 * Generate the API URL for the given method and id.
			 * @param state
			 * @returns
			 */
			apiUrl(state: S) {
				return (method: string, id: string | undefined) => {
					switch (method) {
						case Method.INDEX:
						case Method.CREATE:
							return this.apiPath;

						case Method.UPDATE:
						case Method.VIEW:
						case Method.DELETE:
							if (!id) {
								throw new Error('Method requires an id');
							}

							return this.apiPath + '/' + id;

						case Method.RESTORE:
							if (!id) {
								throw new Error('Method requires an id');
							}

							return this.apiPath + '/' + id + '/restore';

						case Method.DUPLICATE:
							if (!id) {
								throw new Error('Method requires an id');
							}
							return this.apiPath + '/' + id + '/duplicate';

						default:
							throw new Error('Method not implemented');
					}
				};
			},

			/**
			 * Get global query parameters that must be appended to every request.
			 * @param state
			 * @returns
			 */
			globalQueryParameters(state: S): { [key: string]: String | number } {
				return {};
			},

			/**
			 * Get the default mask that should be included in every request.
			 * @param state
			 * @returns
			 */
			defaultMask(state: S): string[] {
				return [ '*' ];
			},

			/**
			 * @returns number
			 */
			defaultRecordsPerPage(): number {
				return 10;
			},

			/**
			 *
			 * @param state
			 * @returns
			 */
			hasDefaultFilterApplied(state: S): boolean {
				if (!state._filter) {
					return true;
				}

				// @ts-ignore Typescript doesn't know that getDefaultFilter() is implemented in the actual store.
				return state._filter.equals(this.getDefaultFilter());
			},

			/**
			 * Check if the currently set filters includes deleted entries.
			 * (In case it does, do not delete items from the store on delete)
			 */
			doesFilterIncludeArchivedEntries() {
				return false;
			},

		} as _GettersTree<AbstractStoreState<M, F>>,

		actions: {

			/**
			 * Map item data from the API to a model.
			 *
			 * This method must be implemented in the actual store, as it is specific to the model.
			 *
			 * @param data
			 */
			mapToItem(data: any): M {
				throw new Error('mapToItem not implemented');
			},

			/**
			 * Load the data from the API if it has not been loaded yet.
			 * If the data has already been loaded, do nothing.
			 *
			 * This is useful to avoid loading the data multiple times when the store is used in multiple components.
			 * (e.g. when a list and a detail component are used on the same page, both will try to load the data,
			 * but it should only be loaded once)
			 */
			async loadOnce() {
				if (this.loadedOnce) {
					return;
				}

				if (this._loadOncePromise) {
					return this._loadOncePromise;
				}

				this._loadOncePromise = (async () => {
					await this.load();
				})();

				try {
					await this._loadOncePromise;
				} finally {
					this._loadOncePromise = null;
				}
			},

			/**
			 * Load the data from the API and refresh the item collection.
			 *
			 * @param awaitNewDataBeforeResettingList If set to TRUE, the list will not first reset before the new data is loaded.
			 */
			async load(
				awaitNewDataBeforeResettingList: boolean = false
			) {
				if (!awaitNewDataBeforeResettingList) {
					this.items = [];
				}

				this.loading = true;
				const requestId = ++this._loadRequestId;

				try {
					const result = await this._executeIndexRequest();

					// A newer load() was triggered while this request was in-flight — discard.
					if (requestId !== this._loadRequestId) {
						console.warn('AbstractBaseStore: Discarding outdated load result. Avoid loading store data multiple times.');
						return;
					}

					// Safe to apply: this is still the most recent request.
					if (result.meta) {
						this.page = PaginationState.mapFromServer(result.meta);
					}

					this.items = result.items;

					await this.afterLoad(this.items);

					if (this.shouldSortLocally()) {
						this.sortLocally();
					}

					this.loadedOnce = true;
				} finally {
					this.loading = false;
				}
			},

			/**
			 * afterLoad() is called after the items have been loaded from the API and have been added to the store,
			 * but before they are sorted locally.
			 * @param items
			 */
			async afterLoad(items: M[]) {
				// Empty method to be overridden
			},

			/**
			 * On a given page, load additional items.
			 * @returns
			 */
			async loadMore(): Promise<void> {

				if (this.loadingMore) {
					// Join the in-flight request instead of returning immediately, so callers
					// that loop "while hasNext()" wait for it rather than busy-spinning.
					return this._loadMorePromise ?? undefined;
				}

				if (!this.page) {
					return;
				}

				if (!this.page.hasNext()) {
					return;
				}

				this.loadingMore = true;

				// Tie this request to the load() that produced the current item list, so a
				// page that resolves after a newer load() replaced the items is discarded.
				const requestId = this._loadRequestId;

				let page: PaginationState = this.page;
				this.page = page.getNext();

				const promise: Promise<void> = (async () => {
					try {
						const result = await this._executeIndexRequest();

						if (requestId !== this._loadRequestId) {
							console.warn('AbstractBaseStore: Discarding outdated loadMore result; a newer load() replaced the items while it was in flight.');
							return;
						}

						// Actually load the additional items
						const newItems = result.items;

						this.items.push(... newItems);

						await this.afterLoad(newItems);
						if (this.shouldSortLocally()) {
							this.sortLocally();
						}

						if (result.meta) {
							this.page = PaginationState.mapFromServer(result.meta);
						}

						// Adapt page to show 'from' as 1 (as the other items are also still loaded)
						// (we are still on the first page, but the page got longer)
						if (this.page) {
							this.page.from = page.from;
						}
					}
					catch (e: any) {
						if (requestId === this._loadRequestId) {
							this.page = page;
						}
						throw e;
					}
					finally {
						this.loadingMore = false;
						this._loadMorePromise = null;
					}
				})();

				this._loadMorePromise = promise;
				return promise;
			},

			/**
			 *
			 * @returns
			 */
			async _executeIndexRequest() {

				// Must assign to a new object to avoid modifying the global query parameters
				// (which is a getter, but for some reason is cached, causing it to be modified)
				const params = Object.assign({}, this.globalQueryParameters);

				params.mask = this.defaultMask.join(',');
				params.page = this.page ? this.page.currentPage : null;
				params.recordsPerPage = this.defaultRecordsPerPage;

				if (this._filter) {
					Object.assign(params, this._filter.toApiParams());
				}

				if (this.order) {
					params.order = this.order.getApiProperty();
				}

				try {
					const response = await this.apiClient.get(this.apiUrl(Method.INDEX), {
						params: params
					});

					return {
						items: response.data.data.map((itemData: any) => this.mapToItem(itemData)),
						meta: response.data.meta
					};
				} catch (e: any) {
					this.errors = ApiErrorMessageBag.fromAxiosException(e);
					throw this.errors;
				}
			},

			/**
			 * Load the first page of paginated data.
			 */
			async loadFirstPage() {
				this.page = null;
				await this.load();
			},

			/**
			 * Load a specific page of paginated data.
			 */
			async goToPage(page: PaginationState) {
				this.page = page;
				await this.load();
			},

			/**
			 * Find a loaded item in the store by its id.
			 * WARNING: Does not load the item from the backend.
			 * @param id
			 * @returns
			 */
			find(id: string): M | null {
				const item = this.items.find((item) => item.id === id);
				if (!item) {
					return null;
				}

				return item as M;
			},

			findAll(ids: string[]): M[] {
				const items = this.items.filter(
					(item) => {
						return item.id && ids.includes(item.id);
					}
				);

				return items as M[];
			},

			/**
			 * Save a model to the store and the backend.
			 * @param model
			 */
			async save(model: M) {

				return this.superSave(model);

			},

			/**
			 * Actually save a model to the store and the backend.
			 * @param model
			 */
			async superSave(model: M) {

				this.errors = null;

				let response;

				if(model.id) {
					// update existing item
					try {
						response = await this.apiClient.put(
							this.apiUrl(Method.UPDATE, model.id),
							model.asApiDataObject(),
							{
								params: {
									mask : this.defaultMask.join(','),
								}
							}
						);
						model.hydrateWithApiData(response.data.data);
						this.updateStoreItem(model);

					} catch (e: any) {
						this.errors = ApiErrorMessageBag.fromAxiosException(e);
						throw this.errors;
					}

				} else {
					// create new item
					try {
						response = await this.apiClient.post(
							this.apiUrl(Method.INDEX),
							model.asApiDataObject(),
							{
								params: {
									mask : this.defaultMask.join(',')
								}
							}
						);
						model.hydrateWithApiData(response.data.data);
						this.addStoreItem(model);

					} catch (e: any) {
						this.errors = ApiErrorMessageBag.fromAxiosException(e);
						throw this.errors;
					}
				}

				await this.afterResponseWithAltered(response, [ model ]);
				await this.afterSave(model);

			},

			async duplicate(model: M, additionalProperties: { [key: string]: any } = {}) {
				return this.superDuplicate(model, additionalProperties);
			},

			async superDuplicate(model: M, additionalProperties: { [key: string]: any } = {}) {

				// create new item
				try {
					const response = await this.apiClient.post(
						this.apiUrl(Method.DUPLICATE, model.id),
						{
							...model.asApiDataObject(),
							...additionalProperties,
						},
						{
							params: {
								mask : this.defaultMask.join(','),
							}
						}
					);
					model.hydrateWithApiData(response.data.data);
					this.addStoreItem(model);

				} catch (e: any) {
					this.errors = ApiErrorMessageBag.fromAxiosException(e);
					throw this.errors;
				}

				await this.afterSave(model);
				return model;

			},

			/**
			 * Save a model to the store and the backend.
			 * @param model
			 */
			async bulkSave(model: M) {

				return this.superBulkSave(model);

			},

			async superBulkSave(
				models: M[],
				additionalParameters: { [key: string]: string }  = {}
			) {

				this.clearErrors();

				try {
					const response = await this.apiClient.post(
						this.apiUrl(Method.INDEX),
						models.map((model) => model.asApiDataObject()),
						{
							params: {
								mask : this.defaultMask.join(','),
								...additionalParameters
							},
							headers: {
								'X-Bulk': 'true'
							}
						}
					);


					models.map((model, index) => {
						model.hydrateWithApiData(response.data.data[index]);
					});

					this.afterResponseWithAltered(response, models);

				} catch (e: any) {
					this.errors = ApiErrorMessageBag.fromAxiosException(e);
					throw this.errors;
				}

				return models.map((model) => {
					const m = this.updateOrAddStoreItem(model);
					this.afterSave(model);

					return m;
				});
			},

			/**
			 * @param response
			 * @param savedModels the models that were saved by the request that resulted in this response
			 */
			async afterResponseWithAltered(response, savedModels: M[] = [])
			{
				// Do we need to process altered models?
				if (typeof(response.data.altered) !== 'undefined') {
					await this.processAlteredEntries(response.data.altered, savedModels);
				}
			},

			async processAlteredEntries(altered: {
				updated?: { id: string, type: string }[],
				removed?: { id: string, type: string }[],
			}, savedModels: M[] = []) {

				if (altered.updated) {
					await this.afterAlteredUpdated(altered.updated, savedModels);
				}

				if (altered.removed) {
					for (let i = 0; i < altered.removed.length; i++) {
						const deletedId = altered.removed[i].id;
						const deletedType = altered.removed[i].type;

						// Only process entries that are of the same type as the store
						if (deletedType !== this.apiEntityType) {
							continue;
						}

						const model = this.find(deletedId);
						if (model) {
							if (!this.doesFilterIncludeArchivedEntries) {
								this.removeStoreItem(model);
							}
							await this.afterDelete(model);
						}
					}
				}
			},

			/**
			 * Delete an item from the store and the backend.
			 * @param model
			 */
			async delete(model: M) {

				try {
					const response = await this.apiClient.delete(this.apiUrl(Method.DELETE, model.id));
					if (this.doesFilterIncludeArchivedEntries) {
						// Item is not deleted from the store, but it is marked as deleted in the backend.
						model.hydrateWithApiData(response.data.data);
						this.updateStoreItem(model);
					} else {
						this.removeStoreItem(model);
					}

				} catch (e: any) {
					this.errors = ApiErrorMessageBag.fromAxiosException(e);
					throw this.errors;
				}

				await this.afterDelete(model);

			},

			async restore(model: M) {

				try {
					const response = await this.apiClient.post(
						this.apiUrl(Method.RESTORE, model.id),
					);
					model.hydrateWithApiData(response.data.data);
					this.updateStoreItem(model);

				} catch (e: any) {
					this.errors = ApiErrorMessageBag.fromAxiosException(e);
					throw this.errors;
				}

				await this.afterSave(model);

			},

			async bulkDelete(itemsToDelete: M[]) {
				return await this.superBulkDelete(itemsToDelete);
			},

			async superBulkDelete(itemsToDelete: M[]) {

				const response = await this.apiClient.delete(this.apiPath, {
					params: {
						ids: [ itemsToDelete.map(item => item.id) ].join(','),
						mask: this.defaultMask.join(',')
					}
				});

				if (!this.doesFilterIncludeArchivedEntries) {
					itemsToDelete.forEach(
						(item) => this.removeStoreItem(item)
					);
				}

			},

			/**
			 * Load data from the API and refresh the model.
			 * @param model
			 */
			async refresh(model: M) {
				const response = await this.apiClient.get(this.apiUrl(Method.VIEW, model.id), {
					params: {
						mask: this.defaultMask.join(',')
					}
				});

				model.hydrateWithApiData(response.data.data);
			},

			/**
			 * Load a single item from the API by its id.
			 * The item is NOT added to the store's item list (which may be a paginated subset),
			 * so it will not receive updates applied to store items afterwards.
			 * @param id
			 */
			async loadById(id: string): Promise<M> {
				try {
					const response = await this.apiClient.get(this.apiUrl(Method.VIEW, id), {
						params: {
							mask: this.defaultMask.join(',')
						}
					});

					return this.mapToItem(response.data.data);
				} catch (e: any) {
					throw ApiErrorMessageBag.fromAxiosException(e);
				}
			},

			/**
			 * Update the value of a loaded item in the store.
			 * @param model
			 */
			updateStoreItem(model: M): M {
				var storeIndex = this.items.findIndex(
					(item) => {
						if (model.id) {
							return item.id === model.id;
						} else {
							// If the model has no id, we assume it is a new model and we compare the whole object.
							return this.representsSameModel(item, model);
						}
					}
				);

				// It is possible that the model is not in the store; in that case, we don't need to update it.
				if (storeIndex < 0) {
					return model;
				}

				this.updateStoreItemFromNewItem(this.items[storeIndex], model);

				this.onUpdateStoreItem(model);
				this.scheduleOnStoreChangedDispatch();
				return ref(this.items[storeIndex]).value;
			},

			updateStoreItemFromNewItem(existingModel: M, newData: M) {
				// I don't think this is the best way but, I'm afraid of editing it for all stores right now.
				// A better approach is to use the objects 'setFromModel' method, but not all models have that.
				existingModel.hydrateWithApiData(newData.asApiDataObject());
			},

			/**
			 * Add a loaded item into the store
			 * @param model
			 */
			addStoreItem(model: M): M {

				// Add the model at the correct position in the list (based on the order)
				// This is kinda wonky as it assumes all records are loaded while this could be paginated.
				if (!this.order) {

					// Add to the beginning of the list if no order is set
					this.items.unshift(ref(model).value);

				} else {

					const orderProperty = this.order.property;
					const orderDirection = this.order.direction;

					const orderPropertyValue = ref(model).value[orderProperty] as any;

					if (!orderProperty || !orderPropertyValue) {

						// Just add the item at the beginning of the list
						this.items.unshift(ref(model).value);

					} else {

						// Find the index where the item should be inserted
						let index = this.items.findIndex((item: any) => {

							return this.orderCompare(item, model) > 0;

						});

						if (index < 0) {
							index = this.items.length;
						}

						this.items.splice(index, 0, ref(model).value);

					}

				}

				this.onAddStoreItem(model);
				this.scheduleOnStoreChangedDispatch();
				return model;
			},

			/**
			 * Get the value of the property that is used for ordering for a given item.
			 * This is used in the orderCompare method to compare two items based on the order property.
			 * @param item
			 * @param order Optional order parameter to use instead of the store's current order
			 */
			orderGetPropertyValue(item: M, order?: OrderParameter): any {
				const o = order ?? this.order;

				// @ts-ignore
				return item[o.property];
			},

			/**
			 * Compare two items based on the order property.
			 * @param item1
			 * @param item2
			 * @param order Optional order parameter to use instead of the store's current order
			 */
			orderCompare(item1: M, item2: M, order?: OrderParameter)
			{
				const o = order ?? this.order;
				const orderDirection = o.direction;

				const v1 = this.orderGetPropertyValue(item1, o);
				const v2 = this.orderGetPropertyValue(item2, o);

				let result;
				if (
					typeof(v1) === 'string' &&
					typeof(v2) === 'string' &&
					v1 && v2
				) {
					result = (v1 as string).localeCompare(v2, undefined,  { sensitivity: 'base' })
				} else {
					result = v1 - v2;
				}

				if (orderDirection === 'asc') {
					return result;
				} else {
					return -result;
				}
			},

			/**
			 * Apply the order parameter to the currently loaded items.
			 * (E.g. useful when some values in the backend represent a i18n 'placeholder' value that is not suitable for ordering,
			 * and the actual string to order by is only available in the frontend)
			 */
			sortLocally(): void {
				this.items.sort((a, b) => this.orderCompare(a, b));
			},

			/**
			 * Get the items in the store.
			 *
			 * @param asStoreObjectReference If true, returns a reference to the live store items array.
			 * If false, returns a sorted copy based on the provided orderParameter. Use a copy if for example you only
			 * need show the items, and in a particular order, without affect (the sorting of) the items in the store.
			 * Be aware though that any changes to those objects will not be automatically reflected in the store.
			 * @param orderParameter The order parameter to use when sorting. Must be null when asStoreObjectReference is true.
			 */
			getItems(asStoreObjectReference: boolean = true, orderParameter: OrderParameter | null = null): UnwrapRef<M[]> {
				if (asStoreObjectReference) {
					if (orderParameter !== null) {
						throw new Error('orderParameter must be null when asStoreObjectReference is true');
					}
					return this.items;
				}

				if (!orderParameter) {
					throw new Error('orderParameter is required when asStoreObjectReference is false');
				}

				return [...this.items]
					.sort((a, b) => this.orderCompare(a, b, orderParameter));
			},

			/**
			 * Remove a loaded item from the store
			 * (does not affect entries in the backend)
			 * @param model
			 */
			removeStoreItem(model: M) {
				const index = this.items.findIndex((item) => item.id === model.id);
				if (index >= 0) {
					this.items.splice(index, 1);
				}

				this.onRemoveStoreItem(model);
				this.scheduleOnStoreChangedDispatch();
			},

			/**
			 * Check if the item is already in the store.
			 * If it is, update it.
			 * If it is not, add it.
			 * @param model
			 */
			updateOrAddStoreItem(model: M): M {
				const storeIndex = this.items.findIndex(
					(item) => this.representsSameModel(item, model)
				);

				if (storeIndex >= 0) {
					return this.updateStoreItem(model);
				} else {
					return this.addStoreItem(model);
				}
			},

			/**
			 * Warning: do not make this a getter, as getter values are cached.
			 * @returns
			 */
			getCurrentFilter(): F {
				if (!this._filter) {
					throw new Error('No filter set');
				}
				return this._filter.clone() as F;
			},

			/**
			 * Apply filter and reload data.
			 * @param filter FilterDef
			 * @returns
			 */
			async applyFilter(filter: F) {
				return await this.superApplyFilter(filter);
			},

			/**
			 * Helper method to apply filter and reload data.
			 * Do not call directly. Use applyFilter instead.
			 * This is a workaround in order to be able to 'override' applyFilters in specific stores.
			 * @param filter
			 */
			async superApplyFilter(filter: F) {

				this._filter = ref(filter).value;

				// Reset pagination as well.
				await this.loadFirstPage();

				// Return the cloned filter for convenience
				return this.getCurrentFilter();
			},

			/**
			 * Reset the filters to their default values and reload data.
			 * @returns void
			 */
			async resetFilter() {
				return await this.superResetFilter();
			},

			/**
			 * Reset filter to default and reload data.
			 * Do not call directly. Use resetFilter instead.
			 * This is a workaround in order to be able to 'override' resetFilter in specific stores.
			 */
			async superResetFilter() {
				if (!this._defaultFilter) {
					throw new Error('No default filter set');
				}

				const filter = this._defaultFilter.clone() as F;

				return this.applyFilter(filter);
			},

			/**
			 * Reset the filter back to the initial filter and reload data.
			 * @returns
			 */
			async applyInitialFilter() {
				if (!this._initialFilter) {
					throw new Error('No initial filter set');
				}

				const filter = this._initialFilter.clone() as F;
				return this.applyFilter(filter);
			},

			/**
			 * Apply order and reload data.
			 * @param order
			 */
			applyOrder(order: OrderParameter) {
				this.superApplyOrder(order);
			},

			/**
			 * Check if the store should sort the items locally after loading them from the backend.
			 * If false, the backend is expected to return the items in the correct order based on the order parameter.
			 * If true, the store will sort the items locally based on the order parameter after loading them from the backend.
			 *
			 * The default is false, as it is more efficient to let the backend sort the items, especially when pagination is used.
			 */
			shouldSortLocally(): boolean {
				return false;
			},

			/**
			 * Actually apply order and reload data.
			 * Do not call directly. Use applyOrder instead.
			 * This is a workaround in order to be able to 'override' applyOrder in specific stores.
			 * @param order
			 */
			async superApplyOrder(order: OrderParameter) {
				this.order = order;
				await this.loadFirstPage();
			},

			/**
			 * Clear the error message.
			 * (call before a save / update / delete action)
			 */
			clearErrors() {
				this.errors = null;
			},

			async afterSave(model: M) {
				// Empty method to be overridden
			},

			/**
			 * afterAlteredUpdated() is called when a response lists other entities that were updated on the
			 * server as a side effect of the request (the 'altered.updated' property).
			 * @param updated
			 * @param savedModels the models that were saved by the request
			 */
			async afterAlteredUpdated(updated: { id: string, type: string }[], savedModels: M[]) {

			},

			async afterDelete(model: M) {
				// Empty method to be overridden
			},

			/**
			 * Return a clone of the default filter.
			 * (The default filter is the base filter that is used when the store is created;
			 * it should represent the default selection before a user makes changes)
			 *
			 * WARNING: Do NOT move this method to a getter, as it will be cached and the filter will not be cloned.
			 * @returns
			 */
			getDefaultFilter(): F {
				if (!this._defaultFilter) {
					throw new Error('Default filter not set');
				}
				return this._defaultFilter.clone() as F;
			},

			setDefaultFilter(filter: F) {
				this._defaultFilter = (filter.clone() as F) as UnwrapRef<F>;
			},

			/**
			 * Return a clone of the initial filter.
			 * (The initial filter is the filter that is used when the store is created;
			 * it represents any user-specific filter that was stored in for example local storage)
			 *
			 * WARNING: Do NOT move this method to a getter, as it will be cached and the filter will not be cloned.
			 * @param state
			 * @returns
			 */
			getInitialFilter(): F {
				if (!this._initialFilter) {
					throw new Error('Initial filter not set');
				}
				return this._initialFilter.clone() as F;
			},

			/**
			 * Schedule a dispatch of the onStoreChanged method.
			 * This is used to avoid multiple dispatches in the same tick.
			 */
			scheduleOnStoreChangedDispatch() {
				if (!this._onStoreChangeTimeout) {
					this._onStoreChangeTimeout = setTimeout(() => {
						this._onStoreChangeTimeout = null;
						this.onStoreChanged();
					}, 0);
				}
			},

			/**
			 * This check is used to see if two models are considered 'the same', from a backend point of view.
			 * This is used to determine if a model should be updated or added to the store.
			 *
			 * The difference with the 'equals' method that is implemented in the models is that this method
			 * does not check for equality of all properties, but only the id (or other properties if the store
			 * implements this method in a different way). An example is the Goals store, where two goals
			 * are considered the same if their 'external' (source_id, source_parent etc) properties are the same.
			 *
			 * @param storeItem
			 * @param otherItem
			 */
			representsSameModel(storeItem: Model, otherItem: Model)
			{
				if (storeItem.id && otherItem.id) {
					return storeItem.id === otherItem.id;
				}

				return false;
			},

			/**
			 * Overrideable methods
			 */
			onStoreChanged() {},

			onAddStoreItem(model: M) {},

			onRemoveStoreItem(model: M) {},

			onUpdateStoreItem(model: M) {},

		}

	}

}
